import { useEffect, useRef, useState } from "react";
import type { RunRequest, RunResponse } from "@/lib/sandbox-worker";

export function useSandbox() {
  const [output, setOutput] = useState(
    "Ready. Enter a specification and click Run.",
  );
  const [isRunning, setIsRunning] = useState(false);
  const workerRef = useRef<Worker | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function dispose() {
    workerRef.current?.terminate();
    workerRef.current = null;
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
  }

  useEffect(() => dispose, []);

  function run(source: string) {
    if (workerRef.current) return;
    setIsRunning(true);
    setOutput("Running…");

    function finish(result: string) {
      dispose();
      setOutput(result);
      setIsRunning(false);
    }

    try {
      // Each run has its own runtime, so a WASM trap cannot poison later runs.
      const worker = new Worker(
        new URL("./sandbox-worker.js", document.baseURI),
        { type: "module" },
      );
      workerRef.current = worker;
      worker.onmessage = (event: MessageEvent<RunResponse>) =>
        finish(event.data.output);
      worker.onerror = (event) => {
        event.preventDefault();
        finish(
          `Unable to run specification: ${event.message || "WebAssembly worker failed."}`,
        );
      };
      worker.onmessageerror = () =>
        finish("Unable to read the verification result. Please try again.");
      timerRef.current = setTimeout(
        () =>
          finish(
            "Verification timed out after 30 seconds. Try a simpler specification.",
          ),
        30_000,
      );
      const request: RunRequest = {
        source,
        moduleUrl: new URL("./wasm/sandbox_wasm.js", document.baseURI).href,
      };
      worker.postMessage(request);
    } catch (error) {
      finish(
        `Unable to start verification: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  return { output, isRunning, run };
}
